import { tss } from 'tss-react/mui';
import { Typography, Button, Divider, SvgIcon, Grid } from '@mui/material';
import { useState } from 'react';

import wizardOwlUrl from 'Images/file-manager-getting-started/owl-wand.svg';
import studiousOwlUrl from 'Images/file-manager-getting-started/studious-owl.svg';

import { type DocumentType, graphql } from '@/gql';

import { Or2Button } from '@/components/button/Or2Button';

import { env } from '@/env';

import { FileManagerAutoCreateFoldersDialog } from './getting-started/FileManagerAutoCreateFoldersDialog';

const FILE_MANAGER_GETTING_STARTED_FILES_FOR_OWNER_FRAGMENT = graphql(`
  fragment fileManagerGettingStartedFilesForOwnerFields on FilesForOwner {
    owner {
      ... on Group {
        _id
        name
        referenceName
      }
      ... on Service {
        _id
        name
      }
    }
  }
`);

interface Props {
  filesForOwner: DocumentType<
    typeof FILE_MANAGER_GETTING_STARTED_FILES_FOR_OWNER_FRAGMENT
  >;
}

const useStyles = tss.create(({ theme }) => {
  return {
    root: {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(4),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '10em',
    },

    divider: {
      width: '25%',
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),
    },

    standalone: {
      marginTop: theme.spacing(2),
    },

    wizard: {
      marginTop: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },

    learnMoreButton: {
      justifyContent: 'center',

      [theme.breakpoints.up('md')]: {
        justifyContent: 'flex-start',
      },
    },
  };
});

export const FileManagerGettingStarted = ({ filesForOwner }: Props) => {
  const { classes } = useStyles();
  const [autoCreateOpen, setAutoCreateOpen] = useState(false);

  const { owner } = filesForOwner;
  const { name: ownerName, _id: ownerId } = owner ?? {};

  const groupReferenceName =
    owner?.__typename === 'Group' ? owner.referenceName : null;

  return (
    <div className={classes.root}>
      <Typography variant="h5" align="center" sx={{ paddingX: 4 }}>
        Get started with the documents section
        {ownerName ? <> for {ownerName}</> : null}
      </Typography>
      <Typography variant="subtitle1" color="textSecondary">
        You can just drag and drop files and folders here
      </Typography>
      {ownerId && (
        <>
          <Typography
            variant="body1"
            color="textSecondary"
            className={classes.standalone}
          >
            or
          </Typography>
          <div className={classes.wizard}>
            <SvgIcon
              component={wizardOwlUrl}
              alt="Owl with wand"
              viewBox="0 -58 250 330"
              sx={{
                fontSize: '96px',
              }}
            />
            <Or2Button size="large" onClick={() => setAutoCreateOpen(true)}>
              Automatically create folders
              {groupReferenceName ? <> for your {groupReferenceName}</> : null}
            </Or2Button>
            <FileManagerAutoCreateFoldersDialog
              filesForOwner={filesForOwner}
              groupReferenceName={groupReferenceName}
              open={autoCreateOpen}
              onClose={() => setAutoCreateOpen(false)}
            />
          </div>
        </>
      )}

      <Divider className={classes.divider} />
      <Grid container alignItems="center" justifyContent="center" spacing={2}>
        <Typography variant="h6" textAlign="center">
          Learn more
        </Typography>
        <Grid
          container
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={0}
        >
          <Grid>
            <SvgIcon
              component={studiousOwlUrl}
              alt="Studious owl"
              viewBox="-32 0 240 240"
              sx={{ fontSize: { xs: '96px', md: '128px' } }}
            />
          </Grid>
          <Grid container size={{ xs: 12, md: 4 }}>
            <Button
              href={`${env.NEXT_PUBLIC_HELP_URL}/en/articles/9685604`}
              rel="noopener noreferrer"
              target="_blank"
              fullWidth
              className={classes.learnMoreButton}
            >
              Get an overview of the documents section
            </Button>
            <Button
              href={`${env.NEXT_PUBLIC_HELP_URL}/en/articles/9690899`}
              rel="noopener noreferrer"
              target="_blank"
              fullWidth
              className={classes.learnMoreButton}
            >
              How to upload or create a file or folder
            </Button>
            <Button
              fullWidth
              href={`${env.NEXT_PUBLIC_HELP_URL}/en/articles/1650308`}
              rel="noopener noreferrer"
              target="_blank"
              className={classes.learnMoreButton}
            >
              The recommended GovernorHub file and folder structure
            </Button>
            <Button
              href={`${env.NEXT_PUBLIC_HELP_URL}/en/collections/1854219`}
              rel="noopener noreferrer"
              target="_blank"
              fullWidth
              className={classes.learnMoreButton}
            >
              All document help articles...
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};
